import { useEffect, useRef, useState } from 'react';
import { Terminal as TerminalIcon, Play, Square, FolderOpen, FileText, Loader2, AlertCircle, CheckCircle2, Trash2 } from 'lucide-react';
import { motion } from 'motion/react';
import { runCollect, checkPython, type PythonCheck, type RunResult } from '../lib/tauri';

/**
 * TerminalPanel — drives the Python CLI sidecar from the Tauri desktop shell.
 *
 * What it does:
 *   - Settings inputs: python_path, cli_path, working_directory (persisted to
 *     localStorage so reopening Tauri restores the user's setup).
 *   - "Test interpreter" button calls check_python invoke → shows version.
 *   - Quick command buttons: status / search / list workspaces.
 *   - Free-form args input → runs `python collect.py <args>` and shows
 *     stdout / stderr / exit code in a terminal-styled panel.
 *
 * Only renders inside Tauri (App.tsx hides the route in browser).
 */

const STORAGE_KEY = 'collect:cli-config';

interface CliConfig {
  pythonPath: string;
  cliPath: string;
  cwd: string;
  defaultWorkspace: string;
}

function loadConfig(): CliConfig {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return { pythonPath: '', cliPath: '', cwd: '', defaultWorkspace: 'my-first-wiki', ...JSON.parse(raw) };
  } catch { /* ignore */ }
  return { pythonPath: 'python', cliPath: '', cwd: '', defaultWorkspace: 'my-first-wiki' };
}

function saveConfig(c: CliConfig) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(c)); } catch { /* ignore */ }
}

export function TerminalPanel() {
  const [config, setConfig] = useState<CliConfig>(() => loadConfig());
  const [pyCheck, setPyCheck] = useState<PythonCheck | null>(null);
  const [pyChecking, setPyChecking] = useState(false);
  const [argsInput, setArgsInput] = useState<string>('status my-first-wiki');
  const [running, setRunning] = useState(false);
  const [history, setHistory] = useState<RunResult[]>([]);
  const [error, setError] = useState<string | null>(null);
  const outputRef = useRef<HTMLDivElement | null>(null);

  // Persist config on change
  useEffect(() => { saveConfig(config); }, [config]);

  // Auto-scroll history to bottom
  useEffect(() => {
    outputRef.current?.scrollTo({ top: outputRef.current.scrollHeight });
  }, [history]);

  const onCheckPython = async () => {
    setPyChecking(true);
    setError(null);
    try {
      const c = await checkPython(config.pythonPath);
      setPyCheck(c);
      if (!c.ok) setError(c.error || `Did not look like Python: "${c.version}"`);
    } catch (e: any) {
      setError(e?.message || String(e));
      setPyCheck({ ok: false, path: config.pythonPath, version: '', error: String(e) });
    } finally {
      setPyChecking(false);
    }
  };

  const run = async (args: string[]) => {
    if (!config.pythonPath || !config.cliPath) {
      setError('Set python_path and cli_path in the config above first.');
      return;
    }
    setRunning(true);
    setError(null);
    try {
      const result = await runCollect(config.pythonPath, config.cliPath, args, config.cwd || undefined);
      setHistory((prev) => [...prev, result].slice(-30));
    } catch (e: any) {
      setError(e?.message || String(e));
    } finally {
      setRunning(false);
    }
  };

  const onRunArgs = () => {
    // Simple shell-like split (preserving quoted strings)
    const parsed: string[] = [];
    const re = /"([^"]*)"|(\S+)/g;
    let m: RegExpExecArray | null;
    while ((m = re.exec(argsInput)) !== null) parsed.push(m[1] ?? m[2]);
    if (parsed.length > 0) run(parsed);
  };

  const onQuickStatus = () => run(['status', config.defaultWorkspace]);
  const onQuickSearch = () => {
    const q = prompt('Search query?');
    if (q) run(['search', config.defaultWorkspace, q]);
  };

  const onClearHistory = () => setHistory([]);

  return (
    <div className="max-w-5xl">
      {/* Hero */}
      <div className="mb-8">
        <h1 className="display text-5xl mb-3">Terminal</h1>
        <p className="text-base" style={{ color: 'rgba(240,253,244,0.6)' }}>
          Drive the Collect Python CLI from the desktop shell. Configure your interpreter + <code className="font-mono" style={{ color: '#34D399' }}>collect.py</code> path,
          then run any subcommand. Output streams below with stdout (paper) and stderr (amber) channels.
        </p>
      </div>

      {/* Config panel */}
      <div className="glass-panel rounded-2xl p-6 mb-4">
        <div className="label mb-3 flex items-center gap-2">
          <FolderOpen size={12} />
          CLI configuration
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <ConfigInput
            label="Python interpreter"
            placeholder="python  •  C:\Python313\python.exe  •  /usr/bin/python3"
            value={config.pythonPath}
            onChange={(v) => setConfig({ ...config, pythonPath: v })}
          />
          <ConfigInput
            label="collect.py path"
            placeholder="C:\...\synapse-collect\apps\cli\collect.py"
            value={config.cliPath}
            onChange={(v) => setConfig({ ...config, cliPath: v })}
          />
          <ConfigInput
            label="Working directory (optional)"
            placeholder="C:\...\synapse-collect\apps\cli"
            value={config.cwd}
            onChange={(v) => setConfig({ ...config, cwd: v })}
          />
          <ConfigInput
            label="Default workspace"
            placeholder="my-first-wiki"
            value={config.defaultWorkspace}
            onChange={(v) => setConfig({ ...config, defaultWorkspace: v })}
          />
        </div>
        <div className="flex items-center gap-3 mt-4">
          <button className="btn btn-ghost" onClick={onCheckPython} disabled={pyChecking || !config.pythonPath}>
            {pyChecking ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
            Test interpreter
          </button>
          {pyCheck && pyCheck.ok && (
            <span className="text-xs flex items-center gap-1.5" style={{ color: '#34D399' }}>
              <CheckCircle2 size={12} />
              <span className="font-mono">{pyCheck.version}</span>
            </span>
          )}
          {pyCheck && !pyCheck.ok && (
            <span className="text-xs flex items-center gap-1.5" style={{ color: '#FBBF24' }}>
              <AlertCircle size={12} />
              <span>{pyCheck.error || pyCheck.version}</span>
            </span>
          )}
        </div>
      </div>

      {/* Run panel */}
      <div className="glass-panel rounded-2xl p-6 mb-4">
        <div className="label mb-3 flex items-center gap-2">
          <TerminalIcon size={12} />
          Run command
        </div>
        <div className="flex items-center gap-2 mb-3">
          <span className="font-mono text-sm flex-none" style={{ color: 'rgba(240,253,244,0.4)' }}>collect.py</span>
          <input
            className="field flex-1 font-mono"
            value={argsInput}
            onChange={(e) => setArgsInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey && !running) onRunArgs(); }}
            placeholder='status my-first-wiki'
            spellCheck={false}
          />
          <button className="btn btn-primary flex-none" onClick={onRunArgs} disabled={running || !argsInput.trim()}>
            {running ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />}
            Run
          </button>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="label">Quick:</span>
          <QuickButton onClick={onQuickStatus} disabled={running}>status {config.defaultWorkspace}</QuickButton>
          <QuickButton onClick={onQuickSearch} disabled={running}>search …</QuickButton>
          <QuickButton onClick={() => run(['hive-list', config.defaultWorkspace])} disabled={running}>hive-list</QuickButton>
          <QuickButton onClick={() => run(['init', 'tmp-' + Date.now()])} disabled={running}>init &lt;tmp&gt;</QuickButton>
        </div>
      </div>

      {/* Errors */}
      {error && (
        <div className="glass-panel rounded-xl p-4 mb-4 flex items-start gap-3" style={{ borderColor: 'rgba(239,68,68,0.4)' }}>
          <AlertCircle size={18} style={{ color: '#F87171' }} className="flex-none mt-0.5" />
          <div className="flex-1 text-sm" style={{ color: '#FCA5A5' }}>{error}</div>
        </div>
      )}

      {/* Output */}
      <div className="glass-panel rounded-2xl overflow-hidden">
        <div className="px-5 py-3 flex items-center gap-2" style={{ borderBottom: '1px solid rgba(16,185,129,0.15)', background: 'rgba(16,185,129,0.03)' }}>
          <FileText size={12} style={{ color: '#10B981' }} />
          <span className="label">Output history</span>
          <span className="label ml-2" style={{ color: 'rgba(240,253,244,0.3)' }}>{history.length} run{history.length === 1 ? '' : 's'}</span>
          <div className="flex-1" />
          {history.length > 0 && (
            <button onClick={onClearHistory} className="text-xs flex items-center gap-1 opacity-50 hover:opacity-100 transition-opacity">
              <Trash2 size={11} /> Clear
            </button>
          )}
        </div>
        <div ref={outputRef} className="overflow-y-auto" style={{ maxHeight: 540, background: '#022C22' }}>
          {history.length === 0 && (
            <div className="px-5 py-12 text-center text-sm" style={{ color: 'rgba(240,253,244,0.35)' }}>
              <Square size={20} style={{ margin: '0 auto 8px', opacity: 0.5 }} />
              No commands run yet. Try a quick command above or type your own args.
            </div>
          )}
          {history.map((r, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              className="px-5 py-4 border-b border-emerald-500/10"
            >
              <div className="font-mono text-xs mb-2 flex items-start gap-2">
                <span className="flex-none" style={{ color: r.code === 0 ? '#34D399' : '#FBBF24' }}>$</span>
                <span className="flex-1 break-all" style={{ color: 'rgba(240,253,244,0.85)' }}>{r.cmd}</span>
                <span className={`pill ${r.code === 0 ? 'pill-done' : 'pill-review'} flex-none`}>
                  {r.code === 0 ? '✓ exit 0' : `× exit ${r.code ?? 'killed'}`}
                </span>
              </div>
              {r.stdout && (
                <pre className="font-mono text-xs whitespace-pre-wrap break-all py-1" style={{ color: 'rgba(240,253,244,0.85)' }}>{r.stdout}</pre>
              )}
              {r.stderr && (
                <pre className="font-mono text-xs whitespace-pre-wrap break-all py-1 mt-1 px-2 rounded" style={{ color: '#FBBF24', background: 'rgba(245,158,11,0.06)' }}>{r.stderr}</pre>
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ConfigInput({ label, placeholder, value, onChange }: {
  label: string; placeholder: string; value: string; onChange: (v: string) => void;
}) {
  return (
    <div>
      <label className="label mb-1.5 block">{label}</label>
      <input
        className="field font-mono"
        type="text"
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        spellCheck={false}
        autoComplete="off"
      />
    </div>
  );
}

function QuickButton({ children, onClick, disabled }: { children: React.ReactNode; onClick: () => void; disabled?: boolean }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className="text-xs font-mono px-2.5 py-1 rounded transition-all"
      style={{
        background: 'rgba(16,185,129,0.08)',
        color: '#34D399',
        border: '1px solid rgba(16,185,129,0.2)',
        opacity: disabled ? 0.4 : 1,
        cursor: disabled ? 'not-allowed' : 'pointer',
      }}
    >
      {children}
    </button>
  );
}
